import { Component } from "@theme/component";
import { ThemeEvents } from "@theme/events";
import { preloadAdjacentSlides, updateVideoPlayback, patchImageSizes } from "@theme/media-gallery";
import { sectionRenderer } from "@theme/section-renderer";
import * as haptic from "@theme/haptic";

const PRODUCT_CARD_SECTION_ID = "product-card";

/**
 * @typedef {object} Refs
 * @property {HTMLAnchorElement} viewport
 * @property {HTMLElement} content
 * @property {HTMLElement} info
 * @property {HTMLElement & { reInit?: () => void, refs?: { viewport?: HTMLElement, slides?: HTMLElement[] } }} [slider]
 * @property {HTMLElement} [pagination]
 * @property {HTMLElement} [quickAdd]
 * @property {HTMLInputElement[]} [swatches]
 */

/**
 * Product card component with media slider and color swatch switching.
 * @extends Component<Refs>
 */
class ProductCardComponent extends Component {
  #abortController = new AbortController();
  /** @type {Map<string, string>} productUrl → section HTML */
  #htmlCache = new Map();
  /** @type {string | undefined} */
  #currentProductUrl;

  connectedCallback() {
    super.connectedCallback();
    if (this.#abortController.signal.aborted) {
      this.#abortController = new AbortController();
    }
    const { signal } = this.#abortController;
    this.addEventListener(ThemeEvents.sliderInit, () => this.#handleSliderInit(), { signal });
    this.addEventListener(ThemeEvents.sliderSelect, (e) => this.#handleSliderSelect(/** @type {CustomEvent} */(e)), { signal });
    this.addEventListener(ThemeEvents.sliderScroll, (e) => this.#handleSliderScroll(/** @type {CustomEvent} */(e)), { signal });
    this.addEventListener("pointerenter", () => {
      const { slider } = this.refs;
      if (!slider) return;
      preloadAdjacentSlides(slider, 0, 1);
      preloadAdjacentSlides(slider, 0, -1);
    }, { signal, once: true });

    this.#currentProductUrl = this.refs.swatches?.find((s) => s.checked)?.dataset.productUrl;

    this.addEventListener("pointerover", (e) => {
      if (/** @type {HTMLElement} */ (e.target).matches(".product-card__swatch")) {
        this.#handleSwatchHover(e);
      }
    }, { signal });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  #handleSliderInit() {
    const { slider } = this.refs;
    const viewport = slider?.refs?.viewport;
    if (viewport) {
      viewport.classList.add("media-gallery__viewport--embla-ready");
    }
    if (slider) updateVideoPlayback(slider, 0);
  }

  /**
   * @param {CustomEvent} event
   */
  #handleSliderSelect(event) {
    const { slider } = this.refs;
    if (!slider) return;
    const index = event.detail?.index ?? 0;
    updateVideoPlayback(slider, index);
    preloadAdjacentSlides(slider, index, 1);
    preloadAdjacentSlides(slider, index, -1);
  }

  /**
   * @param {CustomEvent} event
   */
  #handleSliderScroll(event) {
    const { pagination } = this.refs;
    if (!pagination) return;
    const progress = event.detail?.progress ?? 0;
    pagination.style.setProperty("--slide-progress", String(progress));
  }

  /** @param {Event} event */
  #handleSwatchHover = (event) => {
    const input = /** @type {HTMLInputElement} */ (event.target);
    if (!(input instanceof HTMLInputElement) || input.checked) return;
    const productUrl = input.dataset.productUrl;
    if (!productUrl) return;
    sectionRenderer.getSectionHTML(
      PRODUCT_CARD_SECTION_ID,
      true,
      new URL(productUrl, window.location.origin),
    );
  };

  /** @param {Event} event */
  handleSwatchChange = async (event) => {
    event.stopPropagation();

    const input = /** @type {HTMLInputElement} */ (event.target);
    if (!(input instanceof HTMLInputElement)) return;

    const productUrl = input.dataset.productUrl;
    if (!productUrl) return;

    haptic.light();

    const selectedValue = input.value;
    const isKeyboard = !event.isTrusted || input.matches(":focus-visible");

    // Cache the current product's HTML before swapping (non-blocking)
    const previousUrl = this.#currentProductUrl;
    if (previousUrl && !this.#htmlCache.has(previousUrl)) {
      sectionRenderer.getSectionHTML(
        PRODUCT_CARD_SECTION_ID,
        true,
        new URL(previousUrl, window.location.origin),
      ).then((html) => { this.#htmlCache.set(previousUrl, html); });
    }

    const cached = this.#htmlCache.has(productUrl);
    if (!cached) this.#showShimmer();

    try {
      const html = (cached && this.#htmlCache.get(productUrl))
        || await sectionRenderer.getSectionHTML(
          PRODUCT_CARD_SECTION_ID,
          true,
          new URL(productUrl, window.location.origin),
        );
      this.#htmlCache.set(productUrl, html);
      const doc = new DOMParser().parseFromString(html, "text/html");
      const newCard = doc.querySelector(
        "product-card [data-product-card-content]",
      );

      if (!newCard) return;

      // Stage new content in a detached container so custom elements inside
      // (e.g. slider-component) don't fire connectedCallback twice. We only
      // want initEmbla to run once, and only when connected to the live DOM —
      // otherwise --embla-ready gets set before the second init runs, which
      // breaks the flex-order trick used by model view.
      const preload = document.createElement("div");
      preload.append(...newCard.childNodes);

      // Products without media render a placeholder <svg> carrying the same
      // class, so match the tag too — an SVG has no loading/decode().
      const firstImg = preload.querySelector(
        ".media-gallery__slide:first-child img.media-gallery__image",
      );
      if (firstImg instanceof HTMLImageElement) {
        firstImg.loading = "eager";
        await firstImg.decode().catch(() => { });
      }

      if (isKeyboard) this.focus();

      const { content } = this.refs;
      if (content) {
        content.replaceChildren(...preload.childNodes);
      }
      this.#hideShimmer();

      const newProductCard = /** @type {HTMLElement | null} */ (
        doc.querySelector("product-card")
      );
      if (newProductCard) {
        this.dataset.productId = newProductCard.dataset.productId ?? "";
        // The swapped-in content only covers the media and info blocks — the
        // availability modifier lives on the host element, so sync it manually
        // or a sold-out product stays greyed out after switching color (and back).
        this.classList.toggle(
          "product-card--sold-out",
          newProductCard.classList.contains("product-card--sold-out"),
        );
      }
      this.#updateInfo(doc);
      // updatedCallback() refreshes the ref cache first — otherwise the patches
      // below run against the detached slider from the previous product.
      this.updatedCallback();
      this.#patchImageSizes();
      this.#patchSwatchNames();
      this.#restoreChecked(selectedValue, isKeyboard);

      this.#currentProductUrl = productUrl;

      // Re-init the inner slider-component after content swap
      const { slider } = this.refs;
      if (slider?.reInit) {
        slider.reInit();
      }
    } catch (error) {
      console.error("Product card swatch switch failed:", error);
      window.location.href = productUrl;
    }
  };

  #patchImageSizes() {
    const sizes = this.dataset.sizes;
    if (!sizes) return;
    const { slider } = this.refs;
    if (slider) patchImageSizes(slider, sizes);
  }

  /**
   * Replaces the .product-card__info with the one from the fetched card.
   * @param {Document} doc
   */
  #updateInfo(doc) {
    const { info } = this.refs;
    const newInfo = doc.querySelector("product-card .product-card__info");
    if (info && newInfo) {
      info.replaceWith(newInfo);
    }
  }

  #showShimmer() {
    const { content } = this.refs;
    if (!content) return;
    if (content.querySelector(".product-card__shimmer")) return;

    const shimmer = document.createElement("div");
    shimmer.className = "product-card__shimmer";
    content.appendChild(shimmer);
  }

  #hideShimmer() {
    const { content } = this.refs;
    if (!content) return;
    content.querySelector(".product-card__shimmer")?.remove();
  }

  /**
   * Rewrites swatch radio names to use the parent section ID and index
   * instead of the AJAX section's values.
   */
  #patchSwatchNames() {
    const sectionId =
      this.closest(".shopify-section")?.id?.replace("shopify-section-", "") ??
      "";
    const index = this.dataset.index ?? "";
    const productId = this.dataset.productId ?? "";
    const name = `product-card-color-${sectionId}-${productId}-${index}`;

    const { swatches } = this.refs;
    if (!swatches) return;

    for (const swatch of swatches) {
      swatch.name = name;
    }
  }

  /**
   * Checks the selected swatch and optionally restores focus for keyboard users.
   * @param {string} selectedValue
   * @param {boolean} restoreFocus
   */
  #restoreChecked(selectedValue, restoreFocus) {
    const { swatches } = this.refs;
    if (!swatches) return;

    const swatch = swatches.find((s) => s.value === selectedValue);
    if (swatch) {
      swatch.checked = true;
      if (restoreFocus) swatch.focus();
    }
  }
}

if (!customElements.get("product-card")) {
  customElements.define("product-card", ProductCardComponent);
}
